import React from 'react';

export type IconName =
  | 'script' | 'art' | 'voice'
  | 'play' | 'pause' | 'search' | 'plus' | 'heart' | 'bell' | 'user' | 'x' | 'arrow-right'
  | 'star' | 'lock' | 'award' | 'message' | 'check' | 'music'
  | 'mic' | 'stop' | 'repeat' | 'gift' | 'users';

type Props = {
  name: IconName;
  size?: number;
  className?: string;
  strokeWidth?: number;
  'aria-label'?: string;
};

// Design System v2 アイコン（生絵文字の置き換え）。
// 役割: script=台本 / art=絵 / voice=声。その他はライン系で統一。
const PATHS: Record<IconName, React.ReactNode> = {
  script: (<><path d="M6 3h9l4 4v14H6z" /><path d="M15 3v4h4" /><path d="M9 12h6M9 16h6M9 8h2" /></>),
  art: (<><path d="M12 3a9 9 0 1 0 0 18c1.2 0 2-.9 2-2 0-.5-.2-.9-.5-1.2-.3-.4-.5-.8-.5-1.3 0-1 .8-1.8 1.8-1.8H17a4 4 0 0 0 4-4c0-3.9-4-6.6-9-6.6Z" /><circle cx="7.5" cy="11.5" r="1" /><circle cx="11" cy="7.5" r="1" /><circle cx="15.5" cy="8.5" r="1" /></>),
  voice: (<><path d="M3 12h2M7 8v8M11 4v16M15 7v10M19 10v4" /></>),
  play: (<path d="M7 5v14l12-7z" fill="currentColor" stroke="none" />),
  pause: (<><path d="M8 5v14M16 5v14" /></>),
  search: (<><circle cx="11" cy="11" r="7" /><path d="m20 20-3-3" /></>),
  plus: (<path d="M12 5v14M5 12h14" />),
  heart: (<path d="M12 20s-7-4.5-9.5-9C1 8 2.5 4.5 6 4.5c2 0 3.2 1.3 4 2.5.8-1.2 2-2.5 4-2.5 3.5 0 5 3.5 3.5 6.5C19 15.5 12 20 12 20Z" />),
  bell: (<><path d="M6 9a6 6 0 1 1 12 0c0 5 2 6 2 6H4s2-1 2-6" /><path d="M10 20a2 2 0 0 0 4 0" /></>),
  user: (<><circle cx="12" cy="8" r="4" /><path d="M4 21a8 8 0 0 1 16 0" /></>),
  x: (<path d="M6 6l12 12M18 6L6 18" />),
  'arrow-right': (<path d="M5 12h14M13 6l6 6-6 6" />),
  star: (<path d="M12 3l2.6 5.3 5.9.9-4.3 4.1 1 5.8L12 17.8 6.8 19.2l1-5.8L3.5 9.2l5.9-.9z" />),
  lock: (<><rect x="5" y="11" width="14" height="9" rx="2" /><path d="M8 11V8a4 4 0 0 1 8 0v3" /></>),
  award: (<><circle cx="12" cy="9" r="5" /><path d="M9 13.5 8 21l4-2 4 2-1-7.5" /></>),
  message: (<path d="M21 15a2 2 0 0 1-2 2H8l-4 4V6a2 2 0 0 1 2-2h13a2 2 0 0 1 2 2z" />),
  check: (<path d="M5 12l5 5L20 6" />),
  music: (<><path d="M9 18V5l10-2v12" /><circle cx="6" cy="18" r="3" /><circle cx="16" cy="16" r="3" /></>),
  mic: (<><rect x="9" y="3" width="6" height="11" rx="3" /><path d="M5 11a7 7 0 0 0 14 0M12 18v3" /></>),
  stop: (<rect x="6" y="6" width="12" height="12" rx="2" />),
  repeat: (<><path d="M17 2l4 4-4 4" /><path d="M3 11V9a4 4 0 0 1 4-4h14" /><path d="M7 22l-4-4 4-4" /><path d="M21 13v2a4 4 0 0 1-4 4H3" /></>),
  gift: (<><rect x="3" y="8" width="18" height="4" rx="1" /><path d="M12 8v13M5 12v9h14v-9" /><path d="M12 8S10 3 7.5 4.5 9 8 12 8zM12 8s2-5 4.5-3.5S15 8 12 8z" /></>),
  users: (<><circle cx="9" cy="8" r="3.5" /><path d="M3 21a6 6 0 0 1 12 0" /><path d="M16 5.5a3.5 3.5 0 0 1 0 6.5M21 21a6 6 0 0 0-4-5.6" /></>),
};

export const Icon: React.FC<Props> = ({ name, size = 20, className = '', strokeWidth = 1.8, ...rest }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={strokeWidth}
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    aria-hidden={rest['aria-label'] ? undefined : true}
    role={rest['aria-label'] ? 'img' : undefined}
    {...rest}
  >
    {PATHS[name]}
  </svg>
);

export default Icon;
